// src/features/dashboard/hooks/useProductivityReport.ts

import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import { useManagerDate } from '../../../context/ManagerDateContext';
import { formatApiDate } from '../../../utils/date.utils';

import {
  ProductivityKpiRequest,
  ProductivityKpiResponse,
  TeamProductivityResponse,
  ProductivityTrendResponse,
  AssociateProductivityResponse,
  ProductiveAppMixResponse,
  ProductivityDistributionResponse,
} from '../../../types/Manager/productivityReport.types';

import {
  getProductivityKpiCards,
  getTeamProductivity,
  getProductivityTrend,
  getAssociateProductivity,
  getProductiveAppMix,
  getProductivityDistribution,
} from '../../../services/Manager/productivityReport.service';

interface UseProductivityReportProps {
  managerEmployeeCode: string;
  teamLeadEmployeeCode?: string | null;
  searchText?: string | null;
  shiftName?: string | null;
  geo?: string | null;
}

/* =========================================================
 * Format WTD label
 *
 * API:
 * {
 *   workDate: "2026-08-17T00:00:00",
 *   dayName: "Monday"
 * }
 *
 * Result:
 * Mon17
 * Tue18
 * Wed19
 * Thu20
 * Fri21
 * ========================================================= */

const formatTrendDay = (
  workDate: string,
  dayName: string,
): string => {
  const safeDate = String(workDate ?? '');
  const safeDayName = String(dayName ?? '');

  let dayNumber = '';

  /*
   * Handle:
   * 2026-08-17T00:00:00
   */
  if (safeDate.includes('T')) {
    const datePart = safeDate.split('T')[0];

    const parts = datePart.split('-');

    if (parts.length === 3) {
      dayNumber = parts[2];
    }
  }

  /*
   * Handle:
   * 2026-08-17
   */
  if (!dayNumber && safeDate.includes('-')) {
    const parts = safeDate.split('-');

    if (parts.length === 3) {
      dayNumber = parts[2];
    }
  }

  /*
   * Remove leading zero.
   *
   * 07 -> 7
   * 17 -> 17
   */
  dayNumber = dayNumber.replace(/^0+/, '');

  /*
   * Monday -> Mon
   * Tuesday -> Tue
   * Wednesday -> Wed
   * Thursday -> Thu
   * Friday -> Fri
   */
  const shortDay = safeDayName
    .trim()
    .substring(0, 3);

  return `${shortDay}${dayNumber}`;
};

const useProductivityReport = ({
  managerEmployeeCode,
  teamLeadEmployeeCode = null,
  searchText = null,
  shiftName = null,
  geo = null,
}: UseProductivityReportProps) => {

const {
  fromDate,
  toDate,
} = useManagerDate();

  const [loading, setLoading] =
    useState(true);

  /* =========================================================
   * KPI
   * ========================================================= */

  const [kpiCards, setKpiCards] =
    useState<ProductivityKpiResponse | null>(
      null,
    );

  /* =========================================================
   * TEAM PRODUCTIVITY
   * ========================================================= */

  const [
    teamProductivity,
    setTeamProductivity,
  ] = useState<TeamProductivityResponse[]>(
    [],
  );

  /* =========================================================
   * WTD TREND
   * ========================================================= */

  const [
    productivityTrend,
    setProductivityTrend,
  ] = useState<ProductivityTrendResponse[]>(
    [],
  );

  /* =========================================================
   * ASSOCIATES
   * ========================================================= */

  const [associates, setAssociates] =
    useState<AssociateProductivityResponse[]>(
      [],
    );

  /* =========================================================
   * PRODUCTIVE APP MIX
   * ========================================================= */

  const [appMix, setAppMix] =
    useState<ProductiveAppMixResponse[]>(
      [],
    );

  /* =========================================================
   * PRODUCTIVITY DISTRIBUTION
   * ========================================================= */

  const [
    distribution,
    setDistribution,
  ] = useState<ProductivityDistributionResponse[]>(
    [],
  );

  /* =========================================================
   * ERRORS
   * ========================================================= */

  const [kpiError, setKpiError] =
    useState<string | null>(null);

  const [teamError, setTeamError] =
    useState<string | null>(null);

  const [trendError, setTrendError] =
    useState<string | null>(null);

  const [
    associateError,
    setAssociateError,
  ] = useState<string | null>(null);

  const [appMixError, setAppMixError] =
    useState<string | null>(null);

  const [
    distributionError,
    setDistributionError,
  ] = useState<string | null>(null);

  /* =========================================================
   * FETCH DATA
   * ========================================================= */

  const fetchData = useCallback(
    async () => {
      if (!managerEmployeeCode) {
        setLoading(false);
        return;
      }

      setLoading(true);

      setKpiError(null);
      setTeamError(null);
      setTrendError(null);
      setAssociateError(null);
      setAppMixError(null);
      setDistributionError(null);

      const formattedFromDate =
        formatApiDate(fromDate);

      const formattedToDate =
        formatApiDate(toDate);

      /* =====================================================
       * COMMON REQUEST
       * ===================================================== */

      const commonRequest = {
        managerEmployeeCode,
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        teamLeadEmployeeCode,
      };

      /* =====================================================
       * KPI REQUEST
       * ===================================================== */

      const kpiRequest: ProductivityKpiRequest = {
        managerEmployeeCode,
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        teamLeadEmployeeCode,
      };

      /* =====================================================
       * ASSOCIATE REQUEST
       * ===================================================== */

      const associateRequest = {
        ...commonRequest,
        searchText,
        shiftName,
        geo,
      };

      /* =====================================================
       * API CALLS
       * ===================================================== */

      const [
        kpiResult,
        teamResult,
        trendResult,
        associateResult,
        appMixResult,
        distributionResult,
      ] = await Promise.allSettled([
        getProductivityKpiCards(
          kpiRequest,
        ),

        getTeamProductivity(
          commonRequest,
        ),

        getProductivityTrend(
          commonRequest,
        ),

        getAssociateProductivity(
          associateRequest,
        ),

        getProductiveAppMix(
          commonRequest,
        ),

        getProductivityDistribution(
          commonRequest,
        ),
      ]);

      /* =====================================================
       * KPI
       * ===================================================== */

      if (
        kpiResult.status ===
        'fulfilled'
      ) {
        setKpiCards(
          kpiResult.value,
        );
      } else {
        setKpiCards(null);

        setKpiError(
          'Unable to load KPI Cards.',
        );
      }

      /* =====================================================
       * TEAM PRODUCTIVITY
       * ===================================================== */

      if (
        teamResult.status ===
        'fulfilled'
      ) {
        setTeamProductivity(
          Array.isArray(
            teamResult.value,
          )
            ? teamResult.value
            : [],
        );
      } else {
        setTeamProductivity([]);

        setTeamError(
          'Unable to load Team Productivity.',
        );
      }

      /* =====================================================
       * WTD TREND
       *
       * IMPORTANT:
       *
       * API:
       *
       * Monday   + 2026-08-17
       * Tuesday  + 2026-08-18
       * Wednesday + 2026-08-19
       * Thursday + 2026-08-20
       * Friday   + 2026-08-21
       *
       * UI:
       *
       * Mon17
       * Tue18
       * Wed19
       * Thu20
       * Fri21
       * ===================================================== */

      if (
        trendResult.status ===
        'fulfilled'
      ) {
        const rawTrend =
          Array.isArray(
            trendResult.value,
          )
            ? trendResult.value
            : [];

        const formattedTrend =
          rawTrend.map(
            (item) => ({
              ...item,

              /*
               * Keep original API fields.
               *
               * The chart can use dayLabel
               * after we format it below.
               */
              dayName:
                formatTrendDay(
                  item.workDate,
                  item.dayName,
                ),

              workDate:
                String(
                  item.workDate ?? '',
                ),

              currentProductivity:
                Number(
                  item.currentProductivity ??
                    0,
                ),

              previousProductivity:
                Number(
                  item.previousProductivity ??
                    0,
                ),
            }),
          );

        console.log(
          'Productivity Trend API:',
          rawTrend,
        );

        console.log(
          'Productivity Trend Formatted:',
          formattedTrend,
        );

        setProductivityTrend(
          formattedTrend,
        );
      } else {
        setProductivityTrend([]);

        setTrendError(
          'Unable to load WTD Trend.',
        );
      }

      /* =====================================================
       * ASSOCIATES
       * ===================================================== */

      if (
        associateResult.status ===
        'fulfilled'
      ) {
        setAssociates(
          Array.isArray(
            associateResult.value,
          )
            ? associateResult.value
            : [],
        );
      } else {
        setAssociates([]);

        setAssociateError(
          'Unable to load Associate Productivity.',
        );
      }

      /* =====================================================
       * PRODUCTIVE APP MIX
       * ===================================================== */

      if (
        appMixResult.status ===
        'fulfilled'
      ) {
        setAppMix(
          Array.isArray(
            appMixResult.value,
          )
            ? appMixResult.value
            : [],
        );
      } else {
        setAppMix([]);

        setAppMixError(
          'Unable to load Productive App Mix.',
        );
      }

      /* =====================================================
       * PRODUCTIVITY DISTRIBUTION
       * ===================================================== */

      if (
        distributionResult.status ===
        'fulfilled'
      ) {
        setDistribution(
          Array.isArray(
            distributionResult.value,
          )
            ? distributionResult.value
            : [],
        );
      } else {
        setDistribution([]);

        setDistributionError(
          'Unable to load Productivity Distribution.',
        );
      }

      setLoading(false);
    },
    [
      managerEmployeeCode,
      fromDate,
      toDate,
      teamLeadEmployeeCode,
      searchText,
      shiftName,
      geo,
    ],
  );

  /* =========================================================
   * LOAD WHEN FILTER / DATE CHANGES
   * ========================================================= */

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* =========================================================
   * RETURN
   * ========================================================= */

  return {
    loading,

    /* KPI */
    kpiCards,
    kpiError,

    /* Charts */
    teamProductivity,
    productivityTrend,
    associates,
    appMix,
    distribution,

    /* Errors */
    teamError,
    trendError,
    associateError,
    appMixError,
    distributionError,

    /* Refresh */
    refresh: fetchData,
  };
};

export default useProductivityReport;